// The plugin detail README body. It renders UNTRUSTED markdown through the EXACT
// sanitized pipeline the editor uses (@slate/core renderToHast = the
// rehype-sanitize boundary) and hydrates the sanitized hast to React with
// rehype-react, the same way RenderedPreview does. We add NO sanitizer of our
// own and never weaken the boundary. Two README-specific policies sit on top:
//   - images load ONLY from the plugin's bundled assets (bundledImageSrc),
//   - links open through a guarded host handler instead of navigating.
// Sanitize has already stripped <script>, event handlers, javascript: hrefs, and
// every data: URL (incl. data:image/svg+xml), so a hostile README is inert.

import { useMemo, type ComponentType, type JSX, type ReactElement } from 'react';
import { renderToHast } from '@slate/core';
import rehypeReact from 'rehype-react';
import { unified, type Processor } from 'unified';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';
import type { Root as HastRoot, Element as HastElement } from 'hast';

export interface PluginReadmeProps {
  markdown: string;
  resolveAsset(relPath: string): string | null;
  onOpenLink?(href: string): void;
}

type ImgProps = JSX.IntrinsicElements['img'] & { node?: HastElement };
type AnchorProps = JSX.IntrinsicElements['a'] & { node?: HastElement };

/** Pure: a loadable src ONLY for a bundled relative asset; '' otherwise. */
export function bundledImageSrc(
  src: unknown,
  resolveAsset: (relPath: string) => string | null,
): string {
  if (typeof src !== 'string' || src === '') return '';
  // Reject anything with a scheme (http:, data:, javascript:, ...), a
  // protocol-relative //host, or an absolute /path. Only a bundle-relative
  // path is eligible, and only if the source actually bundles it.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(src) || src.startsWith('//') || src.startsWith('/')) {
    return '';
  }
  return resolveAsset(src) ?? '';
}

function makeImg(resolveAsset: (relPath: string) => string | null): ComponentType<ImgProps> {
  return function ReadmeImg({ node: _node, src, alt, ...rest }: ImgProps): ReactElement {
    const safe = bundledImageSrc(src, resolveAsset);
    return <img {...rest} alt={typeof alt === 'string' ? alt : ''} src={safe} />;
  };
}

function makeAnchor(onOpenLink?: (href: string) => void): ComponentType<AnchorProps> {
  return function ReadmeAnchor({ node: _node, href, children, ...rest }: AnchorProps): ReactElement {
    return (
      <a
        {...rest}
        href={typeof href === 'string' ? href : undefined}
        onClick={(e) => {
          if (typeof href !== 'string' || href === '') return;
          e.preventDefault();
          onOpenLink?.(href);
        }}
      >
        {children}
      </a>
    );
  };
}

type RehypeReactProcessor = Processor<undefined, undefined, undefined, HastRoot, ReactElement>;

export function PluginReadme({ markdown, resolveAsset, onOpenLink }: PluginReadmeProps): ReactElement {
  const element = useMemo(() => {
    const tree = renderToHast(markdown); // sanitized hast (the security boundary)
    const processor = unified().use(rehypeReact, {
      Fragment,
      jsx,
      jsxs,
      passNode: true,
      components: {
        img: makeImg(resolveAsset),
        a: makeAnchor(onOpenLink),
      },
    }) as RehypeReactProcessor;
    return processor.stringify(tree);
  }, [markdown, resolveAsset, onOpenLink]);

  return (
    <div className="mp-readme" data-testid="plugin-readme">
      {element}
    </div>
  );
}
